import { create } from 'zustand';
import { devtools } from 'zustand/middleware';

const useAppStore = create(
  devtools((set, get) => ({
    // Sidebar state
    sidebarCollapsed: false,
    toggleSidebar: () => set((state) => {
      const newState = !state.sidebarCollapsed;
      console.log('%c🔄 [Store] Toggling sidebar:', 'color: #9C27B0; font-weight: bold', 
        state.sidebarCollapsed ? 'expanding' : 'collapsing', '→', newState);
      return { sidebarCollapsed: newState };
    }),

    // Agency selection
    agencies: [],
    selectedAgencies: [],
    setAgencies: (agencies) => set({ agencies }),
    toggleAgency: (agencyId) => set((state) => {
      const isSelected = state.selectedAgencies.includes(agencyId);
      return {
        selectedAgencies: isSelected
          ? state.selectedAgencies.filter(id => id !== agencyId)
          : [...state.selectedAgencies, agencyId]
      };
    }),
    setSelectedAgencies: (agencies) => set({ selectedAgencies: agencies }),
    clearSelectedAgencies: () => set((state) => ({ 
      selectedAgencies: [],
      regulations: [],  // Clear regulations when clearing agencies
      provisionsAnalysis: {}, // Clear provisions too
      selectedRegulations: [], // Clear selected regulations
      activeRegulationId: null // Clear active regulation
    })),

    // Search
    searchQuery: '',
    setSearchQuery: (query) => set({ searchQuery: query }),

    // Statute filtering
    selectedStatutes: [],
    toggleStatute: (statute) => set((state) => {
      const isSelected = state.selectedStatutes.includes(statute);
      return {
        selectedStatutes: isSelected
          ? state.selectedStatutes.filter(s => s !== statute)
          : [...state.selectedStatutes, statute]
      };
    }),
    clearSelectedStatutes: () => set({ selectedStatutes: [] }),
    getUniqueStatutes: () => {
      const state = get();
      const statutes = new Set();
      state.regulations.forEach(reg => {
        if (reg.authorizingStatute) {
          statutes.add(reg.authorizingStatute);
        }
      });
      return Array.from(statutes).sort();
    },

    // Regulations
    regulations: [],
    selectedRegulations: [],
    activeRegulationId: null,
    setRegulations: (regulations) => {
      // Calculate SCOTUS violation counts for each regulation
      const regsWithViolationCounts = regulations.map(reg => {
        const provisions = get().provisionsAnalysis[reg.id] || [];
        
        let loperBrightCount = 0;
        let sackettCount = 0;
        let westVirginiaCount = 0;
        
        provisions.forEach(prov => {
          if (prov.loper_bright_violation) loperBrightCount++;
          if (prov.sackett_violation) sackettCount++;
          if (prov.west_virginia_violation) westVirginiaCount++;
        });
        
        return {
          ...reg,
          loper_bright_violations: loperBrightCount,
          sackett_violations: sackettCount,
          west_virginia_violations: westVirginiaCount,
          total_scotus_violations: loperBrightCount + sackettCount + westVirginiaCount
        };
      });
      
      set({ regulations: regsWithViolationCounts });
    },
    selectRegulation: (regulationId) => set((state) => {
      const isSelected = state.selectedRegulations.includes(regulationId);
      if (isSelected) {
        const newSelection = state.selectedRegulations.filter(id => id !== regulationId);
        return {
          selectedRegulations: newSelection,
          activeRegulationId: newSelection.length > 0 ? newSelection[0] : null
        };
      } else {
        return {
          selectedRegulations: [...state.selectedRegulations, regulationId],
          activeRegulationId: regulationId
        };
      }
    }),
    setActiveRegulation: (regulationId) => set({ activeRegulationId: regulationId }),
    clearSelectedRegulations: () => set({ selectedRegulations: [], activeRegulationId: null }),

    // View mode
    viewMode: 'provisions', // 'provisions', 'cba', 'rewrite', 'consolidated'
    setViewMode: (mode) => {
      console.log('%c🔄 [AppStore] ViewMode changing:', 'color: #9C27B0; font-weight: bold', {
        from: get().viewMode,
        to: mode,
        validModes: ['provisions', 'cba', 'rewrite', 'consolidated']
      });
      set({ viewMode: mode });
      console.log('%c✅ [AppStore] ViewMode changed to:', 'color: #10B981; font-weight: bold', mode);
    },

    // Consolidation
    consolidatedDraft: null,
    isConsolidating: false,
    setConsolidatedDraft: (draft) => set({ consolidatedDraft: draft }),
    setIsConsolidating: (isConsolidating) => set({ isConsolidating }),

    // Vulcan Consolidation Insights
    consolidationMode: false,
    consolidationThreshold: 'moderate', // 'conservative', 'moderate', 'aggressive'
    regulationGroups: [],
    selectedGroup: null,
    groupsLoading: false,
    setConsolidationMode: (mode) => set({ 
      consolidationMode: mode,
      selectedGroup: null // Clear selection when toggling modes
    }),
    setConsolidationThreshold: (threshold) => set({ consolidationThreshold: threshold }),
    setRegulationGroups: (groups) => set({ regulationGroups: groups }),
    setSelectedGroup: (groupId) => set({ selectedGroup: groupId }),
    setGroupsLoading: (loading) => set({ groupsLoading: loading }),

    // CBA data
    cbaData: {},
    setCBAData: (regulationId, data) => set((state) => ({
      cbaData: { ...state.cbaData, [regulationId]: data }
    })),

    // Provisions analysis
    provisionsAnalysis: {},
    setProvisionsAnalysis: (regulationId, analysis) => set((state) => {
      const newProvisionsAnalysis = { ...state.provisionsAnalysis, [regulationId]: analysis };
      
      // Update SCOTUS violation counts for this regulation
      const regulation = state.regulations.find(r => r.id === regulationId);
      if (regulation) {
        let loperBrightCount = 0;
        let sackettCount = 0;
        let westVirginiaCount = 0;
        
        analysis.forEach(prov => {
          if (prov.loper_bright_violation) loperBrightCount++;
          if (prov.sackett_violation) sackettCount++;
          if (prov.west_virginia_violation) westVirginiaCount++;
        });
        
        const updatedRegulations = state.regulations.map(reg => 
          reg.id === regulationId 
            ? {
                ...reg,
                loper_bright_violations: loperBrightCount,
                sackett_violations: sackettCount,
                west_virginia_violations: westVirginiaCount,
                total_scotus_violations: loperBrightCount + sackettCount + westVirginiaCount
              }
            : reg
        );
        
        return {
          provisionsAnalysis: newProvisionsAnalysis,
          regulations: updatedRegulations
        };
      }
      
      return { provisionsAnalysis: newProvisionsAnalysis };
    }),
    setAllProvisionsAnalysis: (allProvisions) => set((state) => {
      // Update SCOTUS violation counts for all regulations
      const updatedRegulations = state.regulations.map(reg => {
        const provisions = allProvisions[reg.id] || [];
        
        let loperBrightCount = 0;
        let sackettCount = 0;
        let westVirginiaCount = 0;
        
        provisions.forEach(prov => {
          if (prov.loper_bright_violation) loperBrightCount++;
          if (prov.sackett_violation) sackettCount++;
          if (prov.west_virginia_violation) westVirginiaCount++;
        });
        
        return {
          ...reg,
          loper_bright_violations: loperBrightCount,
          sackett_violations: sackettCount,
          west_virginia_violations: westVirginiaCount,
          total_scotus_violations: loperBrightCount + sackettCount + westVirginiaCount
        };
      });
      
      return {
        provisionsAnalysis: allProvisions,
        regulations: updatedRegulations
      };
    }),

    // Loading states
    loadingAgencies: false,
    loadingRegulations: false,
    loadingAnalysis: false,
    setLoadingAgencies: (loading) => set({ loadingAgencies: loading }),
    setLoadingRegulations: (loading) => set({ loadingRegulations: loading }),
    setLoadingAnalysis: (loading) => set({ loadingAnalysis: loading }),

    // Error handling
    errors: [],
    addError: (error) => set((state) => ({ 
      errors: [...state.errors, { id: Date.now(), message: error, timestamp: new Date() }] 
    })),
    removeError: (errorId) => set((state) => ({ 
      errors: state.errors.filter(e => e.id !== errorId) 
    })),
    clearErrors: () => set({ errors: [] }),

    // Filtered regulations based on search and selected agencies
    getFilteredRegulations: () => {
      const state = get();
      
      // Return empty array if no agencies selected
      if (state.selectedAgencies.length === 0) {
        console.log('%c🔍 [Store] No agencies selected, returning empty array', 'color: #FF5722');
        return [];
      }
      
      let filtered = state.regulations;
      
      console.log('%c🔍 [Store] Filtering regulations:', 'color: #FF5722', {
        totalRegulations: state.regulations.length,
        selectedAgencies: state.selectedAgencies,
        firstRegAgencyId: state.regulations[0]?.agencyId,
        sampleRegulations: state.regulations.slice(0, 3).map(r => ({ id: r.id, agencyId: r.agencyId }))
      });

      // Filter by selected agencies (already checked length > 0 above)
      const beforeFilter = filtered.length;
      filtered = filtered.filter(reg => 
        state.selectedAgencies.includes(reg.agencyId)
      );
      console.log('%c🔍 [Store] After agency filter:', 'color: #FF5722', {
        beforeCount: beforeFilter,
        afterCount: filtered.length,
        searchingFor: state.selectedAgencies,
        foundMatches: filtered.slice(0, 3).map(r => ({ id: r.id, agencyId: r.agencyId }))
      });

      // Filter by selected statutes
      if (state.selectedStatutes.length > 0) {
        filtered = filtered.filter(reg => {
          // Check if regulation's statute is in selected statutes
          const hasStatute = reg.authorizingStatute && 
                            state.selectedStatutes.some(selected => 
                              reg.authorizingStatute.includes(selected) || 
                              selected.includes(reg.authorizingStatute)
                            );
          return hasStatute;
        });
      }

      // Filter by search query
      if (state.searchQuery) {
        const query = state.searchQuery.toLowerCase();
        filtered = filtered.filter(reg => 
          reg.title.toLowerCase().includes(query) ||
          reg.number.toLowerCase().includes(query) ||
          reg.authorizingStatute?.toLowerCase().includes(query)
        );
      }

      return filtered;
    },
  }))
);

export default useAppStore;